import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { Navigate, Route, Routes, useSearchParams } from "react-router-dom";

import { useAuth } from "common/context/AuthContext";
import useInvitations from "common/datahooks/useInvitations";
import useWorkspaces from "common/datahooks/useWorkspaces";
import { getWhitelabelCustomization } from "common/helpers/utils";
import useAppInit from "common/hooks/useAppInit";
import BaseLayout from "common/layouts/BaseLayout";
import WorkspaceLayout from "common/layouts/WorkspaceLayout";

import CrashPage from "./common/components/CrashPage";
import useCustomer from "./common/datahooks/useCustomer";
import Login from "./pages/login/Login";
import { baseRoutes, workspaceRoutes } from "./pages/routes";

function App() {
  useAppInit();
  const { isAuthorized } = useAuth();
  const { customerInfo } = useCustomer();
  const { workspaces } = useWorkspaces();
  const { invitations } = useInvitations();
  const [searchParams] = useSearchParams();
  const { whitelabelCustomization } = getWhitelabelCustomization();

  const isAppLoaded =
    (whitelabelCustomization && isAuthorized === false) ||
    (isAuthorized === true && customerInfo && workspaces && invitations);

  useEffect(() => {
    if (isAppLoaded) {
      document.getElementById("loader-container")?.remove();
    }
  }, [isAppLoaded]);

  if (!isAppLoaded) {
    return null;
  }

  let routes: JSX.Element;

  if (whitelabelCustomization) {
    routes = (
      <>
        {/*//TODO: REMOVE WORKSPACE ID FROM URL*/}
        {isAuthorized ? (
          <>
            <Route path="workspaces/:workspaceId" element={<WorkspaceLayout />}>
              {workspaceRoutes.map(({ path, component }) => (
                <Route key={path} path={path} element={component} />
              ))}
            </Route>
            {/*//TODO: CLEAN THIS UP WHEN WORKSPACE ID IS REMOVED*/}
            <Route
              path="*"
              element={
                <Navigate
                  to={`workspaces/${workspaces[0].id}/${workspaceRoutes[0].path}?${searchParams.toString()}`}
                  replace
                />
              }
            />
          </>
        ) : (
          <>
            <Route path="/login" element={<Login />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        )}
      </>
    );
  } else {
    const inviteToken = searchParams.get("invite_token");
    const hasInvitation = !!(inviteToken || invitations.length);

    routes = (
      <>
        {/* Base Routes */}
        <Route element={<BaseLayout />}>
          {baseRoutes.map(({ component, path }) => (
            <Route key={path} path={path} element={component} />
          ))}
          {hasInvitation && (
            <Route
              path="*"
              element={
                <Navigate
                  to={`/invitations/${inviteToken || invitations[0].token}`}
                  replace
                />
              }
            />
          )}
          {customerInfo.show_questionnaire && (
            <Route
              path="*"
              element={<Navigate to="/questionnaire" replace />}
            />
          )}
        </Route>
        {/* Workspace routes */}
        {!customerInfo.show_questionnaire && !hasInvitation && (
          <>
            <Route path="workspaces/:workspaceId" element={<WorkspaceLayout />}>
              {workspaceRoutes.map(({ path, component }) => (
                <Route key={path} path={path} element={component} />
              ))}
            </Route>
            <Route
              path="*"
              element={
                <Navigate
                  to={`workspaces/${workspaces[0].id}/${workspaceRoutes[0].path}?${searchParams.toString()}`}
                  replace
                />
              }
            />
          </>
        )}
      </>
    );
  }

  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError, eventId }) => (
        <CrashPage resetError={resetError} eventId={eventId} />
      )}
    >
      <Routes>{routes}</Routes>
    </Sentry.ErrorBoundary>
  );
}

export default App;
